// Derived, role-specific "whose turn is it" status. Kept React-free and unit-
// tested. The stored company.status / submission.status model and the backend
// compliance rollup are unchanged — this is purely a presentation derivation
// (an extension of awaitingReply.ts to a full three-state model with labels).
import type { SubmissionStatus } from './types.ts';

export type ReviewTurn = 'awaiting_admin' | 'awaiting_vendor' | 'compliant' | 'in_progress';

export type ReviewSubmission = {
  status: SubmissionStatus;
  latest_comment_author?: 'admin' | 'vendor' | null;
};

/**
 * Whether a single submission is currently the VENDOR's turn — i.e. the vendor
 * still owes a response. True when the admin sent it back (rejected) or left the
 * most recent comment. Approved / not_applicable items are "cleared" and never
 * count (a stale admin comment on an approved item does not reopen it). A plain
 * pending item — vendor submitted, no admin response yet — is awaiting ADMIN, so
 * this returns false: there is nothing for the vendor to action.
 */
export function isAwaitingVendor(sub: ReviewSubmission): boolean {
  if (sub.status === 'approved' || sub.status === 'not_applicable') return false;
  return sub.status === 'rejected' || sub.latest_comment_author === 'admin';
}

/**
 * Whether a single submission is currently the ADMIN's turn — i.e. the admin
 * still owes a review. True for an uncleared item (not approved / not
 * not_applicable) that is not awaiting the vendor: a freshly-submitted pending
 * item, or one the vendor replied to last. False once the admin has sent it
 * back (rejected) or commented last (that is the vendor's turn), and false for
 * approved / not_applicable items. Per-item complement of isAwaitingVendor over
 * uncleared submissions; consistent with reviewTurn's awaiting_admin branch.
 */
export function isAwaitingAdmin(sub: ReviewSubmission): boolean {
  if (sub.status === 'approved' || sub.status === 'not_applicable') return false;
  return !isAwaitingVendor(sub);
}

/**
 * Classify a set of submissions (one questionnaire, or a whole vendor).
 * Vendor-priority: if the admin has sent ANYTHING back — a rejection or the most
 * recent comment — the vendor owes a response, even if other items still await
 * admin review. Approved / not_applicable items are "cleared" and never drive
 * the turn (a stale admin comment on an approved item does not reopen it).
 */
export function reviewTurn(subs: ReviewSubmission[]): ReviewTurn {
  let awaitingVendor = false;
  let awaitingAdmin = false;
  let hasUncleared = false;
  for (const s of subs) {
    if (s.status === 'approved' || s.status === 'not_applicable') continue;
    hasUncleared = true;
    if (isAwaitingVendor(s)) awaitingVendor = true;
    else awaitingAdmin = true; // pending (incl. vendor replied last)
  }
  if (awaitingVendor) return 'awaiting_vendor';
  if (awaitingAdmin) return 'awaiting_admin';
  return subs.length > 0 && !hasUncleared ? 'compliant' : 'in_progress';
}

// Vendor-facing banner copy. null = render no banner.
export const VENDOR_REVIEW_LABEL: Record<ReviewTurn, string | null> = {
  awaiting_admin: 'Submitted — under review by Admin',
  awaiting_vendor: 'Action required — please review and update the questions marked below.',
  compliant: 'Compliant — your questionnaire has been approved.',
  in_progress: null,
};

// Admin-facing per-questionnaire pill copy. null = render no pill.
export const ADMIN_REVIEW_LABEL: Record<ReviewTurn, string | null> = {
  awaiting_admin: 'Action required',
  awaiting_vendor: 'Vendor change requested',
  compliant: 'Compliant',
  in_progress: null,
};
